import React, { RefObject } from 'react';
import s from './CompareTable.scss';
import ProductHeader from './ProductHeader/ProductHeader';
import ToolsList from './ToolsList';
import ToolsListSingleProduct from './ToolsListSingleProduct';
import classNames from 'classnames';
import { Trans, useTranslation } from '@wix/wix-i18n-config';
import { ClientOffering } from '../../services/client-offering';
import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';
import { useScrollDetection } from './useScrollDetection';
import { TypeSelectedOffer } from '../../types/general-types';
import { PRODUCT } from './constants';
import { isEntry } from '../../services/window-utils';

export interface CompareTableProps extends InjectedExperimentsProps {
  data: ClientOffering;
  selectedOffer: TypeSelectedOffer;
  tableContent: any;
  scrollRef: RefObject<HTMLDivElement>;
  isFormsEntryPoint: boolean;
}

const CompareTable = (props: CompareTableProps) => {
  const { t } = useTranslation();
  const { data, selectedOffer, tableContent, isFormsEntryPoint } = props;
  const [isHeaderSticky, setHeaderSticky] = React.useState(false);
  const [isHeaderHidden, setHeaderHidden] = React.useState(false);
  const [headerHeight, setHeaderHeight] = React.useState(180);
  const headerRef = React.useRef<HTMLDivElement>(null);
  const stickySentinelTopRef = React.useRef<HTMLDivElement>(null);
  const stickySentinelBottomRef = React.useRef<HTMLDivElement>(null);

  const getHeaderHeightStyle = {
    '--sticky-header-height': `${headerHeight}px`,
  } as React.CSSProperties;

  React.useLayoutEffect(
    () =>
      setHeaderHeight(() => {
        const rects = headerRef.current!.getClientRects();
        return rects.length ? rects.item(0)?.height ?? 180 : 180;
      }),
    [isHeaderSticky],
  );

  useScrollDetection({
    ref: stickySentinelTopRef,
    offset: 0,
    cb: (isSticky) => {
      setHeaderSticky(isSticky);
    },
  });

  useScrollDetection({
    ref: stickySentinelBottomRef,
    offset: 0,
    cb: (isHidden) => {
      setHeaderHidden(isHidden);
    },
  });

  const Badges = (badgeProps: { currentPlan: number; isSaleOn: boolean }) => {
    const { currentPlan, isSaleOn } = badgeProps;

    const getcCurrentPlanStyle = {
      gridColumn: `${4 - currentPlan}/${5 - currentPlan}`,
      borderLeft: currentPlan === 0 && 'none',
    } as React.CSSProperties;

    if (isSaleOn) {
      return (
        <div className={s.grid} style={{ border: 'none' }}>
          <div className={s.saleBadgeUnlimited} data-hook="unlimitedSaleBadge">
            {t('sale.badge.title')}
          </div>
          <div className={s.saleBadgePro} data-hook="proSaleBadge">
            {t('sale.badge.title')}
          </div>
        </div>
      );
    }

    return (
      <div className={s.grid} style={{ border: 'none' }}>
        {currentPlan <= 0 && (
          <div className={s.mostPopularBadge} data-hook="mostPopularBadge">
            {t('package.mostPopular')}
          </div>
        )}
        {(currentPlan === 0 || currentPlan === 1) && (
          <div
            className={s.currentPlanBadge}
            style={getcCurrentPlanStyle}
            data-hook="currentPlanBadge"
          >
            {t('compare.header.badge.currentPlan')}
          </div>
        )}
      </div>
    );
  };

  const ProductsFirstLine = () => {
    const shouldShowTaxInPrice = data.currency.taxType;
    const priceCurrency = data.getCurrency();

    return (
      <div
        className={classNames(s.grid, {
          [s.isSticky]: isHeaderSticky,
          [s.isHidden]: isHeaderHidden,
        })}
      >
        <div className={classNames(s.cell, s.refundCell)} ref={headerRef}>
          <Trans components={{ bold: <span style={{ fontWeight: 'bold' }} /> }}>
            {t('compare.refund')}
          </Trans>
        </div>
        <ProductHeader
          productName={t('package.unlimited.name')}
          subtitle={t('package.unlimited.slogan')}
          isSticky={isHeaderSticky}
          shouldBeHighlighted={!data.products[1].isEnabled}
          priceAmount={
            shouldShowTaxInPrice
              ? data.products[2].priceWithTax
              : data.products[2].price
          }
          discountAmount={
            shouldShowTaxInPrice
              ? data.products[2].cycles[1].discountedPriceWithTax
              : data.products[2].cycles[1].discountedPrice
          }
          currency={priceCurrency}
          taxType={data.currency.taxType}
          isEnabled={data.products[2].isEnabled}
          isSaleOn={!!data.isSaleOn()}
          cycles={data.products[2].cycles}
          selectedOffer={selectedOffer}
          productId={data.products[2].productId}
          productIndex={PRODUCT.UNLIMITED}
        />
        <ProductHeader
          productName={t('package.professional.name')}
          subtitle={t('package.professional.slogan')}
          isSticky={isHeaderSticky}
          shouldBeHighlighted={data.products[1].isEnabled}
          priceAmount={
            shouldShowTaxInPrice
              ? data.products[1].priceWithTax
              : data.products[1].price
          }
          discountAmount={
            shouldShowTaxInPrice
              ? data.products[1].cycles[1].discountedPriceWithTax
              : data.products[1].cycles[1].discountedPrice
          }
          currency={priceCurrency}
          taxType={data.currency.taxType}
          isEnabled={data.products[1].isEnabled}
          isSaleOn={!!data.isSaleOn()}
          isMostPopular={!data.isCurrentProduct.pro()}
          cycles={data.products[1].cycles}
          selectedOffer={selectedOffer}
          productId={data.products[1].productId}
          productIndex={PRODUCT.PRO}
        />
        <ProductHeader
          productName={t('package.basic.name')}
          subtitle={t('package.basic.slogan')}
          isSticky={isHeaderSticky}
          priceAmount={
            shouldShowTaxInPrice
              ? data.products[0].priceWithTax
              : data.products[0].price
          }
          discountAmount={
            shouldShowTaxInPrice
              ? data.products[0].cycles[1].discountedPriceWithTax
              : data.products[0].cycles[1].discountedPrice
          }
          currency={priceCurrency}
          taxType={data.currency.taxType}
          isEnabled={data.products[0].isEnabled}
          cycles={data.products[0].cycles}
          selectedOffer={selectedOffer}
          productId={data.products[0].productId}
          productIndex={PRODUCT.BASIC}
        />
        <ProductHeader
          productName={t('package.free.name')}
          subtitle={t('package.free.slogan')}
          isSticky={isHeaderSticky}
          cycles={[]}
          selectedOffer={selectedOffer}
          productId=""
          productIndex={PRODUCT.FREE}
        />
      </div>
    );
  };

  const toolsList: any[] = Object.entries(tableContent).map(
    ([_key, value]) => value,
  );

  if (isEntry.automations()) {
    toolsList[0].order = 2; // Forms
    toolsList[1].order = 1; // Email Marketing
    toolsList[2].order = 3; // Chat
    toolsList[3].order = 0; // Automations
    toolsList[4].order = 4; // Social Post
    toolsList[5].order = 5; // Video Maker
  } else if (isEntry.forms()) {
    toolsList[0].order = 0; // Forms
    toolsList[1].order = 1; // Email Marketing
    toolsList[2].order = 3; // Chat
    toolsList[3].order = 2; // Automations
    toolsList[4].order = 4; // Social Post
    toolsList[5].order = 5; // Video Maker
  } else if (isEntry.chat()) {
    toolsList[0].order = 3; // Forms
    toolsList[1].order = 1; // Email Marketing
    toolsList[2].order = 0; // Chat
    toolsList[3].order = 2; // Automations
    toolsList[4].order = 4; // Social Post
    toolsList[5].order = 5; // Video Maker
  } else if (isEntry.socialPosts()) {
    toolsList[0].order = 3; // Forms
    toolsList[1].order = 1; // Email Marketing
    toolsList[2].order = 4; // Chat
    toolsList[3].order = 2; // Automations
    toolsList[4].order = 0; // Social Post
    toolsList[5].order = 5; // Video Maker
  } else if (isEntry.videoMaker()) {
    toolsList[0].order = 3; // Forms
    toolsList[1].order = 1; // Email Marketing
    toolsList[2].order = 4; // Chat
    toolsList[3].order = 2; // Automations
    toolsList[4].order = 5; // Social Post
    toolsList[5].order = 0; // Video Maker
  } else {
    toolsList[0].order = 2; // Forms
    toolsList[1].order = 0; // Email Marketing
    toolsList[2].order = 3; // Chat
    toolsList[3].order = 1; // Automations
    toolsList[4].order = 4; // Social Post
    toolsList[5].order = 5; // Video Maker
  }
  toolsList[6].order = 6; // Price Quotes
  toolsList[7].order = 7; // More

  toolsList.sort((a, b) => (a.order > b.order ? 1 : -1));

  return (
    <div className={s.strip}>
      <div
        className={s.root}
        ref={props.scrollRef}
        style={getHeaderHeightStyle}
      >
        <div ref={stickySentinelTopRef} />
        {!isHeaderSticky && (
          <Badges
            currentPlan={data.currentProduct?.productIndex ?? -1}
            isSaleOn={!!data.isSaleOn()}
          />
        )}
        <ProductsFirstLine />
        {isFormsEntryPoint ? (
          <ToolsListSingleProduct
            content={toolsList}
            isUnlimitedHighlighted={!data.products[1].isEnabled}
            isHeaderSticky={isHeaderSticky}
          />
        ) : (
          <ToolsList
            content={toolsList}
            isUnlimitedHighlighted={!data.products[1].isEnabled}
            isHeaderSticky={isHeaderSticky}
          />
        )}
        <div className={s.tableBottom} ref={stickySentinelBottomRef} />
      </div>
    </div>
  );
};

export default withExperiments(CompareTable);
